import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Crown, X, Download, Send } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface SfondiProProps {
  isOpen: boolean;
  onClose: () => void;
}

// Import local Pro wallpapers
import appleAqua from "@/assets/wallpapers/apple-aqua.jpg";
import appleAurora from "@/assets/wallpapers/apple-aurora.jpg";
import appleBronze from "@/assets/wallpapers/apple-bronze.jpg";
import appleChampagne from "@/assets/wallpapers/apple-champagne.jpg";
import appleCoral from "@/assets/wallpapers/apple-coral.jpg";
import appleCrystal from "@/assets/wallpapers/apple-crystal.jpg";
import appleCyan from "@/assets/wallpapers/apple-cyan.jpg";
import appleDeepPurple from "@/assets/wallpapers/apple-deep-purple.jpg";
import appleDesert from "@/assets/wallpapers/apple-desert.jpg";
import appleDynamic from "@/assets/wallpapers/apple-dynamic.jpg";
import appleElectric from "@/assets/wallpapers/apple-electric.jpg";
import appleEmerald from "@/assets/wallpapers/apple-emerald.jpg";
import appleForest from "@/assets/wallpapers/apple-forest.jpg";
import appleGalaxy from "@/assets/wallpapers/apple-galaxy.jpg";
import appleGold from "@/assets/wallpapers/apple-gold.jpg";
import appleGreen from "@/assets/wallpapers/apple-green.jpg";
import appleHolographic from "@/assets/wallpapers/apple-holographic.jpg";
import appleIce from "@/assets/wallpapers/apple-ice.jpg";
import appleIndigo from "@/assets/wallpapers/apple-indigo.jpg";
import appleLavender from "@/assets/wallpapers/apple-lavender.jpg";
import appleMercury from "@/assets/wallpapers/apple-mercury.jpg";
import appleMidnight from "@/assets/wallpapers/apple-midnight.jpg";
import appleMint from "@/assets/wallpapers/apple-mint.jpg";
import appleNebula from "@/assets/wallpapers/apple-nebula.jpg";

// F1 wallpapers
import f1Alpine from "@/assets/wallpapers/f1-alpine.jpg";
import f1AstonAbudhabi from "@/assets/wallpapers/f1-aston-abudhabi.jpg";
import f1AstonMartin from "@/assets/wallpapers/f1-aston-martin.jpg";
import f1FerrariMonza from "@/assets/wallpapers/f1-ferrari-monza.jpg";
import f1Ferrari from "@/assets/wallpapers/f1-ferrari.jpg";
import f1Haas from "@/assets/wallpapers/f1-haas.jpg";
import f1Helmet from "@/assets/wallpapers/f1-helmet.jpg";
import f1MclarenSingapore from "@/assets/wallpapers/f1-mclaren-singapore.jpg";
import f1Mclaren from "@/assets/wallpapers/f1-mclaren.jpg";
import f1MercedesSilverstone from "@/assets/wallpapers/f1-mercedes-silverstone.jpg";
import f1Mercedes from "@/assets/wallpapers/f1-mercedes.jpg";
import f1Pitstop from "@/assets/wallpapers/f1-pitstop.jpg";
import f1RedBull from "@/assets/wallpapers/f1-red-bull.jpg";
import f1RedbullMonaco from "@/assets/wallpapers/f1-redbull-monaco.jpg";
import f1Williams from "@/assets/wallpapers/f1-williams.jpg";

// Macchine (Cars) wallpapers
import carAlfaRomeo from "@/assets/wallpapers/car-alfa-romeo.jpg";
import carAstonDb12 from "@/assets/wallpapers/car-aston-db12.jpg";
import carAstonMartin from "@/assets/wallpapers/car-aston-martin.jpg";
import carAudiRs from "@/assets/wallpapers/car-audi-rs.jpg";
import carBentley from "@/assets/wallpapers/car-bentley.jpg";
import carBmwM8 from "@/assets/wallpapers/car-bmw-m8.jpg";
import carBugatti from "@/assets/wallpapers/car-bugatti.jpg";
import carCorvette from "@/assets/wallpapers/car-corvette.jpg";
import carFerrariSf90 from "@/assets/wallpapers/car-ferrari-sf90.jpg";
import carFerrari from "@/assets/wallpapers/car-ferrari.jpg";
import carFordGt from "@/assets/wallpapers/car-ford-gt.jpg";
import carKoenigsegg from "@/assets/wallpapers/car-koenigsegg.jpg";
import carLamborghiniRevuelto from "@/assets/wallpapers/car-lamborghini-revuelto.jpg";
import carLamborghini from "@/assets/wallpapers/car-lamborghini.jpg";
import carLexusLfa from "@/assets/wallpapers/car-lexus-lfa.jpg";
import carMaserati from "@/assets/wallpapers/car-maserati.jpg";
import carMclaren750s from "@/assets/wallpapers/car-mclaren-750s.jpg";
import carMclaren from "@/assets/wallpapers/car-mclaren.jpg";
import carMercedesAmg from "@/assets/wallpapers/car-mercedes-amg.jpg";
import carNissanGtr from "@/assets/wallpapers/car-nissan-gtr.jpg";
import carPagani from "@/assets/wallpapers/car-pagani.jpg";
import carPorscheGt3 from "@/assets/wallpapers/car-porsche-gt3.jpg";
import carPorsche from "@/assets/wallpapers/car-porsche.jpg";
import carRollsRoyce from "@/assets/wallpapers/car-rolls-royce.jpg";
import carToyotaSupra from "@/assets/wallpapers/car-toyota-supra.jpg";

// Calcio wallpapers
import calcioBall from "@/assets/wallpapers/calcio-ball.jpg";
import calcioBallonDor from "@/assets/wallpapers/calcio-ballon-dor.jpg";
import calcioBicycleKick from "@/assets/wallpapers/calcio-bicycle-kick.jpg";
import calcioBoots from "@/assets/wallpapers/calcio-boots.jpg";
import calcioCelebration from "@/assets/wallpapers/calcio-celebration.jpg";
import calcioCorner from "@/assets/wallpapers/calcio-corner.jpg";
import calcioDribbling from "@/assets/wallpapers/calcio-dribbling.jpg";
import calcioFreekick from "@/assets/wallpapers/calcio-freekick.jpg";
import calcioGloves from "@/assets/wallpapers/calcio-gloves.jpg";
import calcioGoal from "@/assets/wallpapers/calcio-goal.jpg";
import calcioManCity from "@/assets/wallpapers/calcio-man-city.jpg";
import calcioMbappe from "@/assets/wallpapers/calcio-mbappe.jpg";
import calcioMessi from "@/assets/wallpapers/calcio-messi.jpg";
import calcioRealMadrid from "@/assets/wallpapers/calcio-real-madrid.jpg";
import calcioReferee from "@/assets/wallpapers/calcio-referee.jpg";
import calcioRonaldo from "@/assets/wallpapers/calcio-ronaldo.jpg";
import calcioSave from "@/assets/wallpapers/calcio-save.jpg";
import calcioStadium from "@/assets/wallpapers/calcio-stadium.jpg";
import calcioTrophy from "@/assets/wallpapers/calcio-trophy.jpg";
import calcioWorldCup from "@/assets/wallpapers/calcio-world-cup.jpg";

// City wallpapers
import cityNewYork from "@/assets/wallpapers/city-new-york.jpg";
import cityTokyo from "@/assets/wallpapers/city-tokyo.jpg";
import cityParis from "@/assets/wallpapers/city-paris.jpg";
import cityDubai from "@/assets/wallpapers/city-dubai.jpg";
import cityLondon from "@/assets/wallpapers/city-london.jpg";
import citySingapore from "@/assets/wallpapers/city-singapore.jpg";
import cityHongKong from "@/assets/wallpapers/city-hong-kong.jpg";
import cityLosAngeles from "@/assets/wallpapers/city-los-angeles.jpg";
import cityRome from "@/assets/wallpapers/city-rome.jpg";
import cityMilan from "@/assets/wallpapers/city-milan.jpg";

// Nature wallpapers
import natureMountains from "@/assets/wallpapers/nature-mountains.jpg";
import natureBeach from "@/assets/wallpapers/nature-beach.jpg";
import natureForest from "@/assets/wallpapers/nature-forest.jpg";
import natureWaterfall from "@/assets/wallpapers/nature-waterfall.jpg";
import natureAurora from "@/assets/wallpapers/nature-aurora.jpg";
import natureSunset from "@/assets/wallpapers/nature-sunset.jpg";
import natureLavender from "@/assets/wallpapers/nature-lavender.jpg";
import natureLake from "@/assets/wallpapers/nature-lake.jpg";
import natureCherryBlossom from "@/assets/wallpapers/nature-cherry-blossom.jpg";
import natureDesert from "@/assets/wallpapers/nature-desert.jpg";

interface ProWallpaper {
  id: number;
  image: string;
  name: string;
  category: "apple" | "f1" | "macchine" | "calcio" | "citta" | "natura";
}

interface Category {
  id: "all" | "apple" | "f1" | "macchine" | "calcio" | "citta" | "natura";
  label: string;
}

const categories: Category[] = [
  { id: "all", label: "Tutti" },
  { id: "apple", label: "Apple" },
  { id: "f1", label: "F1" },
  { id: "macchine", label: "Macchine" },
  { id: "calcio", label: "Calcio" },
  { id: "citta", label: "Città" },
  { id: "natura", label: "Natura" },
];

// High-quality Pro wallpapers - Premium exclusive content with local assets
const proWallpapers: ProWallpaper[] = [
  // Apple category
  { id: 1, image: appleAqua, name: "Aqua", category: "apple" },
  { id: 2, image: appleAurora, name: "Aurora", category: "apple" },
  { id: 3, image: appleBronze, name: "Bronze", category: "apple" },
  { id: 4, image: appleChampagne, name: "Champagne", category: "apple" },
  { id: 5, image: appleCoral, name: "Coral", category: "apple" },
  { id: 6, image: appleCrystal, name: "Crystal", category: "apple" },
  { id: 7, image: appleCyan, name: "Cyan", category: "apple" },
  { id: 8, image: appleDeepPurple, name: "Deep Purple", category: "apple" },
  { id: 9, image: appleDesert, name: "Desert", category: "apple" },
  { id: 10, image: appleDynamic, name: "Dynamic", category: "apple" },
  { id: 11, image: appleElectric, name: "Electric", category: "apple" },
  { id: 12, image: appleEmerald, name: "Emerald", category: "apple" },
  { id: 13, image: appleForest, name: "Forest", category: "apple" },
  { id: 14, image: appleGalaxy, name: "Galaxy", category: "apple" },
  { id: 15, image: appleGold, name: "Gold", category: "apple" },
  { id: 16, image: appleGreen, name: "Green", category: "apple" },
  { id: 17, image: appleHolographic, name: "Holographic", category: "apple" },
  { id: 18, image: appleIce, name: "Ice", category: "apple" },
  { id: 19, image: appleIndigo, name: "Indigo", category: "apple" },
  { id: 20, image: appleLavender, name: "Lavender", category: "apple" },
  { id: 21, image: appleMercury, name: "Mercury", category: "apple" },
  { id: 22, image: appleMidnight, name: "Midnight", category: "apple" },
  { id: 23, image: appleMint, name: "Mint", category: "apple" },
  { id: 24, image: appleNebula, name: "Nebula", category: "apple" },
  
  // F1 category
  { id: 25, image: f1Alpine, name: "Alpine", category: "f1" },
  { id: 26, image: f1AstonAbudhabi, name: "Aston Abu Dhabi", category: "f1" },
  { id: 27, image: f1AstonMartin, name: "Aston Martin", category: "f1" },
  { id: 28, image: f1FerrariMonza, name: "Ferrari Monza", category: "f1" },
  { id: 29, image: f1Ferrari, name: "Ferrari", category: "f1" },
  { id: 30, image: f1Haas, name: "Haas", category: "f1" },
  { id: 31, image: f1Helmet, name: "Helmet", category: "f1" },
  { id: 32, image: f1MclarenSingapore, name: "McLaren Singapore", category: "f1" },
  { id: 33, image: f1Mclaren, name: "McLaren", category: "f1" },
  { id: 34, image: f1MercedesSilverstone, name: "Mercedes Silverstone", category: "f1" },
  { id: 35, image: f1Mercedes, name: "Mercedes", category: "f1" },
  { id: 36, image: f1Pitstop, name: "Pit Stop", category: "f1" },
  { id: 37, image: f1RedBull, name: "Red Bull", category: "f1" },
  { id: 38, image: f1RedbullMonaco, name: "Red Bull Monaco", category: "f1" },
  { id: 39, image: f1Williams, name: "Williams", category: "f1" },
  
  // Macchine (Cars) category
  { id: 40, image: carAlfaRomeo, name: "Alfa Romeo", category: "macchine" },
  { id: 41, image: carAstonDb12, name: "Aston DB12", category: "macchine" },
  { id: 42, image: carAstonMartin, name: "Aston Martin", category: "macchine" },
  { id: 43, image: carAudiRs, name: "Audi RS", category: "macchine" },
  { id: 44, image: carBentley, name: "Bentley", category: "macchine" },
  { id: 45, image: carBmwM8, name: "BMW M8", category: "macchine" },
  { id: 46, image: carBugatti, name: "Bugatti", category: "macchine" },
  { id: 47, image: carCorvette, name: "Corvette", category: "macchine" },
  { id: 48, image: carFerrariSf90, name: "Ferrari SF90", category: "macchine" },
  { id: 49, image: carFerrari, name: "Ferrari", category: "macchine" },
  { id: 50, image: carFordGt, name: "Ford GT", category: "macchine" },
  { id: 51, image: carKoenigsegg, name: "Koenigsegg", category: "macchine" },
  { id: 52, image: carLamborghiniRevuelto, name: "Lamborghini Revuelto", category: "macchine" },
  { id: 53, image: carLamborghini, name: "Lamborghini", category: "macchine" },
  { id: 54, image: carLexusLfa, name: "Lexus LFA", category: "macchine" },
  { id: 55, image: carMaserati, name: "Maserati", category: "macchine" },
  { id: 56, image: carMclaren750s, name: "McLaren 750S", category: "macchine" },
  { id: 57, image: carMclaren, name: "McLaren", category: "macchine" },
  { id: 58, image: carMercedesAmg, name: "Mercedes AMG", category: "macchine" },
  { id: 59, image: carNissanGtr, name: "Nissan GTR", category: "macchine" },
  { id: 60, image: carPagani, name: "Pagani", category: "macchine" },
  { id: 61, image: carPorscheGt3, name: "Porsche GT3", category: "macchine" },
  { id: 62, image: carPorsche, name: "Porsche", category: "macchine" },
  { id: 63, image: carRollsRoyce, name: "Rolls Royce", category: "macchine" },
  { id: 64, image: carToyotaSupra, name: "Toyota Supra", category: "macchine" },
  
  // Calcio category
  { id: 65, image: calcioBall, name: "Ball", category: "calcio" },
  { id: 66, image: calcioBallonDor, name: "Ballon d'Or", category: "calcio" },
  { id: 67, image: calcioBicycleKick, name: "Bicycle Kick", category: "calcio" },
  { id: 68, image: calcioBoots, name: "Boots", category: "calcio" },
  { id: 69, image: calcioCelebration, name: "Celebration", category: "calcio" },
  { id: 70, image: calcioCorner, name: "Corner", category: "calcio" },
  { id: 71, image: calcioDribbling, name: "Dribbling", category: "calcio" },
  { id: 72, image: calcioFreekick, name: "Free Kick", category: "calcio" },
  { id: 73, image: calcioGloves, name: "Gloves", category: "calcio" },
  { id: 74, image: calcioGoal, name: "Goal", category: "calcio" },
  { id: 75, image: calcioManCity, name: "Man City", category: "calcio" },
  { id: 76, image: calcioMbappe, name: "Mbappé", category: "calcio" },
  { id: 77, image: calcioMessi, name: "Messi", category: "calcio" },
  { id: 78, image: calcioRealMadrid, name: "Real Madrid", category: "calcio" },
  { id: 79, image: calcioReferee, name: "Referee", category: "calcio" },
  { id: 80, image: calcioRonaldo, name: "Ronaldo", category: "calcio" },
  { id: 81, image: calcioSave, name: "Save", category: "calcio" },
  { id: 82, image: calcioStadium, name: "Stadium", category: "calcio" },
  { id: 83, image: calcioTrophy, name: "Trophy", category: "calcio" },
  { id: 84, image: calcioWorldCup, name: "World Cup", category: "calcio" },
  
  // Città (City) category
  { id: 85, image: cityNewYork, name: "New York", category: "citta" },
  { id: 86, image: cityTokyo, name: "Tokyo", category: "citta" },
  { id: 87, image: cityParis, name: "Paris", category: "citta" },
  { id: 88, image: cityDubai, name: "Dubai", category: "citta" },
  { id: 89, image: cityLondon, name: "London", category: "citta" },
  { id: 90, image: citySingapore, name: "Singapore", category: "citta" },
  { id: 91, image: cityHongKong, name: "Hong Kong", category: "citta" },
  { id: 92, image: cityLosAngeles, name: "Los Angeles", category: "citta" },
  { id: 93, image: cityRome, name: "Roma", category: "citta" },
  { id: 94, image: cityMilan, name: "Milano", category: "citta" },
  
  // Natura (Nature) category
  { id: 95, image: natureMountains, name: "Montagne", category: "natura" },
  { id: 96, image: natureBeach, name: "Spiaggia", category: "natura" },
  { id: 97, image: natureForest, name: "Foresta", category: "natura" },
  { id: 98, image: natureWaterfall, name: "Cascata", category: "natura" },
  { id: 99, image: natureAurora, name: "Aurora Boreale", category: "natura" },
  { id: 100, image: natureSunset, name: "Tramonto", category: "natura" },
  { id: 101, image: natureLavender, name: "Lavanda", category: "natura" },
  { id: 102, image: natureLake, name: "Lago", category: "natura" },
  { id: 103, image: natureCherryBlossom, name: "Fiori di Ciliegio", category: "natura" },
  { id: 104, image: natureDesert, name: "Deserto", category: "natura" },
];

const SfondiPro = ({ isOpen, onClose }: SfondiProProps) => {
  const navigate = useNavigate();
  const [selectedWallpaper, setSelectedWallpaper] = useState<ProWallpaper | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<Category["id"]>("all");


  const filteredWallpapers = selectedCategory === "all" 
    ? proWallpapers 
    : proWallpapers.filter(w => w.category === selectedCategory);

  const handleSave = async () => {
    if (!selectedWallpaper) return;
    
    try {
      const response = await fetch(selectedWallpaper.image);
      const blob = await response.blob();
      
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.download = `sfondo-pro-${selectedWallpaper.name.toLowerCase().replace(/\s+/g, '-')}.jpg`;
      link.href = url;
      link.click();
      URL.revokeObjectURL(url);
      
      toast.success("Sfondo Pro salvato!");
    } catch (error) {
      console.error("Error saving wallpaper:", error);
      toast.error("Errore nel salvataggio");
    }
  };

  const handleShare = async () => {
    if (!selectedWallpaper) return;

    try {
      const response = await fetch(selectedWallpaper.image);
      const blob = await response.blob();
      
      if (navigator.canShare && navigator.canShare({ files: [new File([blob], "sfondo-pro.jpg", { type: "image/jpeg" })] })) {
        const file = new File([blob], `sfondo-pro-${selectedWallpaper.name.toLowerCase().replace(/\s+/g, '-')}.jpg`, { type: "image/jpeg" });
        await navigator.share({
          title: `Sfondo Pro Tecly - ${selectedWallpaper.name}`,
          text: `Guarda questo esclusivo sfondo Pro Tecly - ${selectedWallpaper.name}!`,
          files: [file],
        });
        return;
      }
    } catch (error) {
      console.log("File sharing failed, trying link sharing", error);
    }

    const shareData: ShareData = {
      title: `Sfondo Pro Tecly - ${selectedWallpaper.name}`,
      text: `Guarda questo esclusivo sfondo Pro Tecly - ${selectedWallpaper.name}!`,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (error) {
        console.log("Condivisione annullata", error);
      }
    } else {
      toast.info("Condivisione non supportata");
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[150] bg-background overflow-hidden"
    >
      {/* Header */}
      <div className="sticky top-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border/50">
        <div className="flex items-center gap-4 px-4 py-4">
          <motion.button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-muted transition-colors"
            whileHover={{ scale: 1.1, rotate: -8 }}
            whileTap={{ scale: 0.9, rotate: 0 }}
          >
            <ArrowLeft className="w-6 h-6" />
          </motion.button>
          <div className="flex items-center gap-2 flex-1">
            <Crown className="w-6 h-6 text-yellow-500" />
            <h1 className="text-xl font-bold">Sfondi Pro</h1>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="h-[calc(100vh-65px)] overflow-y-auto">
          <div className="pb-8">
            {/* Category Filter */}
            <div className="px-4 pt-4 sticky top-0 z-40 bg-background/80 backdrop-blur-lg">
              <div className="flex gap-2 items-center overflow-x-auto scrollbar-hide pb-3">
                {categories.map((category) => (
                  <motion.button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 flex-shrink-0 ${
                      selectedCategory === category.id
                        ? "bg-gradient-to-r from-yellow-500 to-amber-500 text-white shadow-lg shadow-yellow-500/30"
                        : "bg-muted text-muted-foreground hover:bg-muted/80"
                    }`}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    animate={selectedCategory === category.id ? { 
                      scale: [1, 1.02, 1],
                      transition: { duration: 0.3 }
                    } : {}}
                    layout
                  >
                    {category.label}
                  </motion.button>
                ))}
              </div>
            </div>

            {/* Wallpapers Gallery */}
            <div className="px-4 pt-2">
              <div className="grid grid-cols-2 gap-4">
                {filteredWallpapers.map((wallpaper, index) => (
                  <motion.div
                    key={wallpaper.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.03 }}
                    className="w-full aspect-[9/16] rounded-2xl overflow-hidden cursor-pointer shadow-lg relative group"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setSelectedWallpaper(wallpaper)}
                    layout
                  >
                    <img 
                      src={wallpaper.image} 
                      alt={wallpaper.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    {/* Wallpaper Name Overlay */}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                      <p className="text-white text-sm font-medium">{wallpaper.name}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
      </div>

      {/* Wallpaper Preview Fullscreen */}
      <AnimatePresence>
        {selectedWallpaper && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200]"
          >
            <img 
              src={selectedWallpaper.image}
              alt={selectedWallpaper.name}
              className="w-full h-full object-cover"
            />
            
            <div className="absolute top-0 left-0 right-0 z-10 pt-safe">
              <div className="flex items-center justify-between px-4 py-6">
                <motion.button
                  onClick={() => setSelectedWallpaper(null)}
                  className="p-3 rounded-full bg-black/20 backdrop-blur-xl border border-white/10"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <X className="w-6 h-6 text-white drop-shadow-lg" />
                </motion.button>
                
                <div className="flex items-center gap-3">
                  <motion.button
                    onClick={handleSave}
                    className="p-3 rounded-full bg-black/20 backdrop-blur-xl border border-white/10"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <Download className="w-6 h-6 text-white drop-shadow-lg" />
                  </motion.button>
                  
                  <motion.button
                    onClick={handleShare}
                    className="p-3 rounded-full bg-black/20 backdrop-blur-xl border border-white/10"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <Send className="w-6 h-6 text-white drop-shadow-lg" />
                  </motion.button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default SfondiPro;
